import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { determineTextColor } from '@r6digital/rapidstor-types/react/util/Color';

export const injectSiteTheme = (location: Location, rapidstorMainColor?: string | null): void => {
    const appConfigThemeColor = rapidstorMainColor ?? window.rapidstor_settings?.app_config?.journey?.themeColor ?? location.skin.mainColour;

    const themeMap: Map<string, string> = new Map([
        // accentColours is commonly {} — sites need not set an accent.
        ['--rs-accent', location.skin.accentColours.primary ?? ''],
        ['--rs-theme', appConfigThemeColor],
        ['--rs-highlight', location.skin.highlightColour],
        [
            '--rs-primary-on-background',
            appConfigThemeColor
                ? determineTextColor('#ffffff', appConfigThemeColor)
                : '',
        ],
        [
            '--rs-text-on-primary',
            appConfigThemeColor
                ? determineTextColor(appConfigThemeColor, '#ffffff')
                : '',
        ],
        [
            '--rs-text-on-secondary',
            appConfigThemeColor
                ? determineTextColor(appConfigThemeColor, '#d9d9d9')
                : '',
        ],
    ]);

    themeMap.forEach((value, key) => {
        if (!value) return;
        const current = getComputedStyle(document.documentElement)
            .getPropertyValue(key)
            .trim();
        if (current !== value) {
            document.documentElement.style.setProperty(key, value);
        }
    });
};

const CUSTOM_CSS_STYLE_ID = 'rs-app-config-custom-css';

/**
 * Strips `@import` at-rules from operator-supplied CSS.
 *
 * `@import` is the one construct that is both dangerous and effectively never
 * legitimate in injected scoped styling: it pulls arbitrary external
 * stylesheets at render time (uncontrolled network requests, CSS chaining).
 * `url(...)` and `data:` URIs are intentionally left untouched — operators
 * legitimately rely on them for background images, web fonts, and inline SVG
 * icons, and the CSS author is the trusted party (see {@link injectCustomCss}).
 */
const sanitizeCustomCss = (css: string): string => {
    const sanitized = css.replace(/@import\b[^;]*;?/gi, '');
    if (sanitized !== css) {
        // eslint-disable-next-line no-console
        console.warn(
            '[RapidStor] Stripped @import rule(s) from app_config.customCss',
        );
    }
    return sanitized;
};

/**
 * The elements operator CSS is allowed to reach: the app root plus the two
 * modals that are portaled to `<body>` (the same set App.scss treats as the
 * app). `:where()` adds no specificity, so each scoped rule wins or loses
 * against the app's own styles exactly as it did before it was scoped.
 */
const CUSTOM_CSS_SCOPE_IDS = ['rapidstor-v2-frontend', 'contact-card-modal', 'details-modal'];
const CUSTOM_CSS_SCOPE = `:where(${CUSTOM_CSS_SCOPE_IDS.map((id) => `#${id}`).join(', ')})`;

// CSSRule.type values (the named constants are not on every CSSRule global).
const STYLE_RULE = 1;
const PAGE_RULE = 6;
const KEYFRAMES_RULE = 7;

/**
 * Splits a selector at top-level occurrences of the given characters, leaving
 * anything inside (), [] or quotes alone. Returns the pieces and the separators
 * between them.
 */
const splitTopLevel = (
    selector: string,
    isSeparator: (char: string) => boolean,
): { parts: string[]; separators: string[] } => {
    const parts: string[] = [];
    const separators: string[] = [];
    let depth = 0;
    let quote: string | null = null;
    let current = '';
    for (let i = 0; i < selector.length; i += 1) {
        const char = selector[i];
        if (quote) {
            current += char;
            if (char === '\\') {
                current += selector[i + 1] ?? '';
                i += 1;
            } else if (char === quote) {
                quote = null;
            }
        } else if (char === '"' || char === '\'') {
            quote = char;
            current += char;
        } else if (char === '\\') {
            current += char + (selector[i + 1] ?? '');
            i += 1;
        } else if (char === '(' || char === '[') {
            depth += 1;
            current += char;
        } else if (char === ')' || char === ']') {
            depth -= 1;
            current += char;
        } else if (depth === 0 && isSeparator(char)) {
            parts.push(current);
            separators.push(char);
            current = '';
        } else {
            current += char;
        }
    }
    parts.push(current);
    return { parts, separators };
};

/**
 * The parts of a compound selector outside any (), [] or quotes, with escaped
 * characters masked, so `div:not(#details-modal)` reads as `div:not` and
 * `.a\\#details-modal` has no `#`.
 */
const topLevelOf = (compound: string): string => {
    let depth = 0;
    let quote: string | null = null;
    let out = '';
    for (let i = 0; i < compound.length; i += 1) {
        const char = compound[i];
        if (quote) {
            if (char === '\\') i += 1;
            else if (char === quote) quote = null;
        } else if (char === '"' || char === '\'') {
            quote = char;
        } else if (char === '(' || char === '[') {
            depth += 1;
        } else if (char === ')' || char === ']') {
            depth -= 1;
        } else if (depth === 0) {
            if (char === '\\') {
                // An escaped character is part of a name, never a `#`, `.` or
                // `:` delimiter. A placeholder keeps names from running together.
                out += '_';
                i += 1;
            } else {
                out += char;
            }
        }
    }
    return out;
};

// CSS whitespace only: U+00A0 and other Unicode spaces are identifier
// characters in CSS, so `\s` and `trim()` would split names apart.
const CSS_WHITESPACE = /^[ \t\n\f\r]$/;
const trimCss = (value: string): string => value.replace(/^[ \t\n\f\r]+|[ \t\n\f\r]+$/g, '');

// A compound that describes the host document rather than an element in the
// app: `html`, `body` or `:root`, optionally with more qualifiers
// (`body.dark`, `html[lang="en"]`, `:root:has(.x)`). Pseudo-elements
// (`body::-webkit-scrollbar`) are not page context: nothing can follow them.
const HOST_COMPOUND = /^(?:html|body|:root)(?=$|[.#:[])/i;
const isHostCompound = (compound: string): boolean => {
    const topLevel = topLevelOf(compound);
    return HOST_COMPOUND.test(topLevel) && !topLevel.includes('::');
};

// A compound that is itself one of the scope elements, e.g. `#details-modal`
// or `div#rapidstor-v2-frontend.x`, but not `:not(#details-modal)`.
const SCOPE_ID_COMPOUND = new RegExp(`#(?:${CUSTOM_CSS_SCOPE_IDS.join('|')})(?=$|[.#:[])`);
const isScopeCompound = (compound: string): boolean => SCOPE_ID_COMPOUND.test(topLevelOf(compound));

/**
 * Rewrites one complex selector so it can only match inside the scope.
 *
 * - Leading `html` / `body` / `:root` compounds stay as page context and the
 *   scope is inserted after them, so `body .btn` becomes `body <scope> .btn`
 *   and `:root { --rs-theme: … }` lands on the app root itself.
 * - Selectors that already run through a scope element using only descendant
 *   and child combinators after it are left as written.
 * - Everything else is prefixed: `.navbar` becomes `<scope> .navbar`.
 */
const scopeSelector = (selector: string): string => {
    const trimmed = trimCss(selector);
    if (!trimmed) return trimmed;

    // Tokenise into compounds and combinators. Whitespace around `>`, `+`
    // and `~` is folded into that combinator.
    const { parts, separators } = splitTopLevel(
        trimmed,
        (c) => CSS_WHITESPACE.test(c) || c === '>' || c === '+' || c === '~',
    );
    const compounds: string[] = [];
    const combinators: string[] = [];
    let pending: string | null = null;
    parts.forEach((part, i) => {
        if (part) {
            if (compounds.length) combinators.push(pending ?? ' ');
            compounds.push(part);
            pending = null;
        }
        const sep = separators[i];
        if (sep && !CSS_WHITESPACE.test(sep)) pending = sep;
        else if (sep && pending === null) pending = ' ';
    });

    const lastScoped = compounds.reduce(
        (found, compound, i) => (isScopeCompound(compound) ? i : found),
        -1,
    );
    if (
        lastScoped !== -1
        && combinators.slice(lastScoped).every((c) => c === ' ' || c === '>')
    ) {
        return trimmed;
    }

    let hostCount = 0;
    while (hostCount < compounds.length && isHostCompound(compounds[hostCount])) {
        hostCount += 1;
    }

    const glue = (combinator: string) => (combinator === ' ' ? ' ' : ` ${combinator} `);
    const join = (from: number, to: number) => compounds
        .slice(from, to)
        .reduce((acc, compound, i) => (i === 0 ? compound : acc + glue(combinators[from + i - 1]) + compound), '');

    const host = join(0, hostCount);
    const rest = join(hostCount, compounds.length);
    const afterScope = rest ? glue(hostCount > 0 ? combinators[hostCount - 1] : ' ') + rest : '';
    return `${host ? `${host} ` : ''}${CUSTOM_CSS_SCOPE}${afterScope}`;
};

const scopeSelectorList = (selectorText: string): string => splitTopLevel(selectorText, (c) => c === ',')
    .parts.map(scopeSelector)
    .filter(Boolean)
    .join(', ');

/**
 * Walks a parsed stylesheet and rewrites it so every style rule only applies
 * inside the scope. `@media`, `@supports`, `@layer`, `@container` and other
 * grouping rules are kept and their contents scoped. `@font-face`,
 * `@keyframes` and similar definitions are kept as they only take effect when
 * a scoped rule uses them. `@page` is dropped because it styles the host
 * page's print layout.
 */
type RuleContainer = Pick<CSSStyleSheet, 'cssRules' | 'deleteRule'>;

/**
 * Scopes a style rule in place. Returns false when the rule must be removed.
 */
const scopeStyleRule = (rule: CSSRule): boolean => {
    if (rule.type === PAGE_RULE) return false;
    if (rule.type !== STYLE_RULE) return true;
    const styleRule = rule as CSSStyleRule;
    // Some parsers (jsdom's) read unknown at-rules such as `@page` as style
    // rules with an `@…` selector. Nothing legitimate looks like that, so
    // drop it rather than scope it.
    if (styleRule.selectorText.trim().startsWith('@')) return false;
    const original = styleRule.selectorText;
    const scoped = scopeSelectorList(original);
    styleRule.selectorText = scoped;
    // The setter silently ignores a selector the engine rejects, which would
    // leave the original unscoped one in place. Drop the rule.
    return !(scoped !== original && styleRule.selectorText === original);
};

/**
 * Scopes every rule in a stylesheet or grouping rule (`@media`, `@supports`,
 * `@layer`, …) in place and deletes the ones that must go, so a group's own
 * cssText only carries scoped children. `@keyframes` is left alone.
 */
const scopeRulesIn = (container: RuleContainer): void => {
    for (let i = container.cssRules.length - 1; i >= 0; i -= 1) {
        const rule = container.cssRules[i];
        // CSSPageRule also has cssRules (its margin boxes), so it is ruled
        // out here and dropped by scopeStyleRule.
        const isGroup = rule.type !== KEYFRAMES_RULE
            && rule.type !== STYLE_RULE
            && rule.type !== PAGE_RULE
            && (rule as CSSGroupingRule).cssRules;
        if (isGroup) {
            scopeRulesIn(rule as CSSGroupingRule);
        } else if (!scopeStyleRule(rule)) {
            container.deleteRule(i);
        }
    }
};

const scopeSheet = (sheet: RuleContainer): string => {
    scopeRulesIn(sheet);
    return Array.from(sheet.cssRules).map((rule) => rule.cssText).join('\n');
};

/**
 * Confines operator CSS to the RapidStor app so it cannot restyle the page
 * it is embedded in. The CSS is parsed with the browser's own parser: a
 * constructable stylesheet where supported, otherwise a `<style>` whose
 * `media="not all"` stops it applying while it is briefly in the document.
 */
export const scopeCustomCss = (css: string): string => {
    if (typeof CSSStyleSheet !== 'undefined' && 'replaceSync' in CSSStyleSheet.prototype) {
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(css);
        return scopeSheet(sheet);
    }
    const style = document.createElement('style');
    style.media = 'not all';
    style.textContent = css;
    document.head.appendChild(style);
    try {
        return style.sheet ? scopeSheet(style.sheet) : '';
    } finally {
        style.remove();
    }
};

/**
 * Injects operator-supplied custom CSS into the document head.
 *
 * Trust model: `css` comes from the tenant journey config
 * (`window.rapidstor_settings.app_config.customCss`), authored by the storage
 * operator in their RapidStor configuration and delivered via the backend
 * journey config / the root element's `data-appConfig` attribute. The operator
 * is trusted — they control their own embedding page — so this is privileged
 * configuration, NOT end-user input.
 *
 * The CSS is written to `<style>.textContent`, so it cannot execute
 * JavaScript. As defence in depth against the residual CSS-injection risk
 * (e.g. uncontrolled external loads), `@import` rules are stripped before
 * injection. Every rule is then scoped to the app root (see
 * {@link scopeCustomCss}) so the CSS can only style RapidStor, never the host
 * page around it. Do not route untrusted / end-user input through this
 * function.
 */
export const injectCustomCss = (css?: string | null): void => {
    const existing = document.getElementById(CUSTOM_CSS_STYLE_ID);
    if (!css) {
        existing?.remove();
        return;
    }
    const safeCss = scopeCustomCss(sanitizeCustomCss(css));
    if (existing) {
        if (existing.textContent !== safeCss) existing.textContent = safeCss;
        return;
    }
    const style = document.createElement('style');
    style.id = CUSTOM_CSS_STYLE_ID;
    style.textContent = safeCss;
    document.head.appendChild(style);
};
